/**
 * React Query hooks for Investor Import functionality
 */

import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { investorImportService } from '@/services/investorImportService';
import type { APIError } from '@/utils/api-client';
import type {
  ImportValidationResult,
  FundMatchingData,
  ExistingInvestorConflict,
  ImportResult,
} from '@/types/investor-import';

// Hook for uploading and validating import file
export function useInvestorImportUpload() {
  return useApiMutation<ImportValidationResult, APIError, File>(
    (file: File) => investorImportService.uploadFile(file),
    {
      mutationKey: ['investorImport', 'upload'],
      successMessage: 'File uploaded and validated successfully',
      errorMessage: 'Failed to upload file',
    }
  );
}

// Hook for matching funds
export function useInvestorImportMatchFunds() {
  return useApiMutation<FundMatchingData[], APIError, string>(
    (sessionId: string) => investorImportService.matchFunds(sessionId),
    {
      mutationKey: ['investorImport', 'matchFunds'],
      showSuccessToast: false, // Silent operation
    }
  );
}

// Hook for updating fund matches
export function useInvestorImportUpdateMatches() {
  return useApiMutation<
    { message: string },
    APIError,
    { sessionId: string; fundMappings: FundMatchingData[] }
  >(
    ({ sessionId, fundMappings }) =>
      investorImportService.updateFundMatches(sessionId, fundMappings),
    {
      mutationKey: ['investorImport', 'updateMatches'],
      successMessage: 'Fund matches saved successfully',
    }
  );
}

// Hook for detecting conflicts
export function useInvestorImportDetectConflicts() {
  return useApiMutation<ExistingInvestorConflict[], APIError, string>(
    (sessionId: string) => investorImportService.detectConflicts(sessionId),
    {
      mutationKey: ['investorImport', 'detectConflicts'],
      showSuccessToast: false, // Silent operation
    }
  );
}

// Hook for resolving conflicts
export function useInvestorImportResolveConflicts() {
  return useApiMutation<
    { message: string },
    APIError,
    { sessionId: string; conflicts: ExistingInvestorConflict[] }
  >(
    ({ sessionId, conflicts }) =>
      investorImportService.resolveConflicts(sessionId, conflicts),
    {
      mutationKey: ['investorImport', 'resolveConflicts'],
      successMessage: 'Conflict resolutions saved',
    }
  );
}

// Hook for executing import
export function useInvestorImportExecute() {
  return useApiMutation<
    ImportResult,
    APIError,
    {
      sessionId: string;
      fundMappings: FundMatchingData[];
      conflictResolutions: ExistingInvestorConflict[];
    }
  >(
    ({ sessionId, fundMappings, conflictResolutions }) =>
      investorImportService.executeImport(
        sessionId,
        fundMappings,
        conflictResolutions
      ),
    {
      mutationKey: ['investorImport', 'execute'],
      successMessage: 'Import completed successfully',
      invalidateQueries: [queryKeys.investors.all, queryKeys.funds.all],
    }
  );
}

// Hook for cleaning up session
export function useInvestorImportDeleteSession() {
  return useApiMutation<{ message: string }, APIError, string>(
    (sessionId: string) => investorImportService.deleteSession(sessionId),
    {
      mutationKey: ['investorImport', 'deleteSession'],
      showSuccessToast: false, // Silent cleanup
    }
  );
}
